import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

// ============================================
// Style constants
// ============================================
const colors = {
  bg: '#111827',
  cardBg: '#1f2937',
  border: '#374151',
  primary: '#B25373',
  primaryHover: '#8E3D59',
  textWhite: '#ffffff',
  textGray300: '#d1d5db',
  textGray400: '#9ca3af',
  textGray500: '#6b7280',
  danger: '#ef4444',
  dangerLight: '#fca5a5',
  dangerBg: 'rgba(239, 68, 68, 0.1)',
  warningBg: 'rgba(245, 158, 11, 0.1)',
  warningText: '#fcd34d',
  warningIcon: '#f59e0b',
  successBg: 'rgba(34, 197, 94, 0.1)',
  successText: '#86efac',
  successIcon: '#22c55e',
};

// ============================================
// ForgotPassword Component
// ============================================

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { forgotPassword, error, isLoading, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');

  const validateEmail = () => {
    if (!email.trim()) {
      setEmailError('آدرس ایمیل الزامی است');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('فرمت ایمیل نامعتبر است');
      return false;
    }

    setEmailError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (validateEmail()) {
      try {
        await forgotPassword(email);
        setSubmitted(true);
      } catch (err) {
        // Error handled by context
      }
    }
  };

  const inputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    paddingRight: 40,
    paddingLeft: 12,
    paddingTop: 10,
    paddingBottom: 10,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    backgroundColor: colors.cardBg,
    color: colors.textWhite,
    fontSize: 14,
    fontFamily: 'inherit',
    direction: 'ltr',
    textAlign: 'left',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.bg,
        padding: '48px 16px',
      }}
    >
      <Box
        sx={{
          maxWidth: 448,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <ShieldIcon sx={{ fontSize: 40, color: '#fff' }} />
          </Box>
          <Typography
            sx={{
              fontSize: 30,
              fontWeight: 800,
              color: colors.textWhite,
              lineHeight: 1.3,
            }}
          >
            بازیابی رمز عبور
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: 14,
              color: colors.textGray400,
            }}
          >
            {submitted
              ? 'لینک بازیابی به ایمیل شما ارسال شد'
              : 'ایمیل خود را وارد کنید تا لینک بازیابی ارسال شود'}
          </Typography>
        </Box>

        {!submitted ? (
          <>
            {/* Error Message */}
            {error && (
              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  backgroundColor: colors.dangerBg,
                }}
              >
                <WarningIcon sx={{ color: colors.danger, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14, color: colors.dangerLight, lineHeight: 1.6 }}>
                  {error}
                </Typography>
              </Box>
            )}

            {/* Email Validation Error */}
            {emailError && (
              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  backgroundColor: colors.warningBg,
                }}
              >
                <WarningIcon sx={{ color: colors.warningIcon, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14, color: colors.warningText, lineHeight: 1.6 }}>
                  {emailError}
                </Typography>
              </Box>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Email */}
              <Box>
                <Typography
                  component="label"
                  htmlFor="email"
                  sx={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 500,
                    color: colors.textGray300,
                    mb: 0.5,
                  }}
                >
                  آدرس ایمیل
                </Typography>
                <Box sx={{ position: 'relative', mt: 0.5 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      right: 0,
                      pr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  >
                    <EmailIcon sx={{ fontSize: 20, color: colors.textGray500 }} />
                  </Box>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = '0 0 0 2px rgba(178, 83, 115, 0.3)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.border;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Box>
              </Box>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: 8,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  color: colors.textWhite,
                  backgroundColor: isLoading ? colors.primaryHover : colors.primary,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) (e.target as HTMLElement).style.backgroundColor = colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) (e.target as HTMLElement).style.backgroundColor = colors.primary;
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                    <span>در حال ارسال...</span>
                  </>
                ) : (
                  'ارسال لینک بازیابی'
                )}
              </button>

              {/* Back to Login */}
              <button
                type="button"
                onClick={() => navigate('/login')}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  color: colors.textGray400,
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
                بازگشت به صفحه ورود
              </button>
            </Box>
          </>
        ) : (
          <>
            {/* Success State */}
            <Box
              sx={{
                borderRadius: 2,
                p: 3,
                backgroundColor: colors.successBg,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(34, 197, 94, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 32, color: colors.successIcon }} />
              </Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: colors.successIcon,
                  mb: 1,
                }}
              >
                لینک بازیابی ارسال شد
              </Typography>
              <Typography sx={{ fontSize: 14, color: colors.successText, lineHeight: 1.8 }}>
                لینک بازیابی رمز عبور به آدرس{' '}
                <strong dir="ltr" style={{ fontFamily: 'monospace' }}>
                  {email}
                </strong>{' '}
                ارسال شد. لطفاً صندوق ایمیل خود را بررسی کنید.
              </Typography>
            </Box>

            {/* Back to Login */}
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 6,
                padding: '10px 16px',
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                fontSize: 14,
                fontFamily: 'inherit',
                color: colors.textGray300,
                backgroundColor: 'transparent',
                cursor: 'pointer',
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              بازگشت به صفحه ورود
            </button>
          </>
        )}

        {/* Security Notice */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 12, color: colors.textGray500, lineHeight: 1.8 }}>
            این بخش امن مدیریت است.
          </Typography>
          <Typography sx={{ fontSize: 12, color: colors.textGray500, lineHeight: 1.8 }}>
            تمام فعالیت‌ها ثبت و نظارت می‌شوند.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
